import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import ArticleCard from '../components/ui/ArticleCard.jsx';
import Badge from '../components/ui/Badge.jsx';
import { ArticleThumbnail, IssueThumbnail } from '../components/ui/CoverThumbnail.jsx';
import { listArticles, listIssues } from '../lib/auth';
import { formatArticleType, normalizeArticle, normalizeIssue } from '../lib/journalFormat';

const ARTICLE_TYPES = [
  ['', 'All Types'],
  ['original-research', 'Original Research'],
  ['review', 'Review'],
  ['systematic-review-meta-analysis', 'Systematic Review and Meta-analysis'],
  ['edge', 'EDGE'],
  ['case-report', 'Case Report/Series'],
  ['others', 'Others'],
];

const TABS = [
  { id: 'articles', label: 'All Articles' },
  { id: 'issues', label: 'Browse by Issue' },
];

function Articles({ initialTab = 'articles' }) {
  const [activeTab, setActiveTab] = useState(initialTab);

  // Articles tab state
  const [articles, setArticles] = useState([]);
  const [q, setQ] = useState('');
  const [type, setType] = useState('');
  const [articlesMessage, setArticlesMessage] = useState('');
  const [loading, setLoading] = useState(true);

  // Issues tab state
  const [issues, setIssues] = useState([]);
  const [selectedIssueId, setSelectedIssueId] = useState('');
  const [year, setYear] = useState('');
  const [issuesMessage, setIssuesMessage] = useState('');

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  useEffect(() => {
    document.title = activeTab === 'issues' ? 'Issues & Archives - JAMSD' : 'Articles - JAMSD';
  }, [activeTab]);

  useEffect(() => {
    const timeout = setTimeout(async () => {
      setLoading(true);
      try {
        const result = await listArticles({ q, type });
        setArticles((result.data || []).map(normalizeArticle));
        setArticlesMessage('');
      } catch (error) {
        setArticlesMessage(error.message);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(timeout);
  }, [q, type]);

  useEffect(() => {
    listIssues({ published: 'true' })
      .then((result) => {
        const nextIssues = (result.data || []).map(normalizeIssue);
        setIssues(nextIssues);
        const firstPublished = nextIssues.find((issue) => !issue.upcoming) || nextIssues[0];
        setSelectedIssueId(firstPublished?.id || '');
      })
      .catch((error) => setIssuesMessage(error.message));
  }, []);

  const years = useMemo(() => [...new Set(issues.map((issue) => issue.year).filter(Boolean))].sort((a, b) => b - a), [issues]);
  const visibleIssues = year ? issues.filter((issue) => String(issue.year) === String(year)) : issues;
  const selectedIssue = issues.find((issue) => issue.id === selectedIssueId);
  const volumes = useMemo(() => {
    const grouped = visibleIssues.reduce((acc, issue) => {
      const key = `${issue.raw.volume}-${issue.year}`;
      if (!acc[key]) acc[key] = { volume: issue.raw.volume, year: issue.year, issues: [] };
      acc[key].issues.push(issue);
      return acc;
    }, {});
    return Object.values(grouped).sort((a, b) => b.year - a.year || b.volume - a.volume);
  }, [visibleIssues]);

  return (
    <>
      <section className="bg-navy pt-24 pb-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="font-sans text-amber text-xs font-semibold uppercase tracking-widest mb-2">Research Archive</p>
          <h1 className="font-display text-white text-3xl md:text-4xl font-bold mb-2">Articles &amp; Issues</h1>
          <p className="font-sans text-slate-light text-base max-w-2xl mb-6">
            Browse published articles by title, abstract, keywords, and type, or explore JAMSD by volume and issue.
          </p>
          <div className="flex gap-1 border-b border-navy-border">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2.5 font-sans text-sm font-semibold rounded-t-lg transition-colors ${
                  activeTab === tab.id
                    ? 'bg-bg-cool text-navy'
                    : 'text-slate-pale hover:text-white'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {activeTab === 'articles' ? (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="bg-white rounded-lg border border-bg-border shadow-sm p-5 mb-8">
            <div className="flex flex-col md:flex-row gap-4">
              <input
                type="search"
                value={q}
                onChange={(event) => setQ(event.target.value)}
                placeholder="Search articles by title, abstract, or keyword..."
                className="flex-1 font-sans text-sm border border-bg-border rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-amber"
              />
              <select value={type} onChange={(event) => setType(event.target.value)} className="md:w-56 font-sans text-sm border border-bg-border rounded-lg px-4 py-2.5">
                {ARTICLE_TYPES.map(([value, label]) => <option key={label} value={value}>{label}</option>)}
              </select>
            </div>
          </div>

          {articlesMessage && <div className="mb-5 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{articlesMessage}</div>}
          <div className="flex items-center justify-between my-6 flex-wrap gap-2">
            <p className="font-sans text-sm text-slate">
              {loading ? 'Loading articles...' : <>Showing <strong className="text-navy">{articles.length}</strong> articles</>}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mb-10">
            {articles.map((article) => <ArticleCard key={article.id} article={article} />)}
          </div>
          {!loading && !articles.length && (
            <div className="rounded-lg border border-bg-border bg-bg-ice p-8 text-center font-sans text-slate">No published articles match the current filters.</div>
          )}
        </div>
      ) : (
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between gap-4 mb-6">
            <h2 className="font-display text-navy text-2xl font-bold">Browse by Volume</h2>
            <select value={year} onChange={(event) => setYear(event.target.value)} className="rounded border border-bg-border bg-white px-4 py-2 font-sans font-semibold text-navy">
              <option value="">All Years</option>
              {years.map((item) => <option key={item} value={item}>{item}</option>)}
            </select>
          </div>

          {issuesMessage && <div className="mb-5 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{issuesMessage}</div>}

          <div className="space-y-5 mb-8">
            {volumes.map((volume) => {
              const articleCount = volume.issues.reduce((sum, issue) => sum + issue.articleCount, 0);
              return (
                <section key={`${volume.volume}-${volume.year}`} className="rounded-lg border border-bg-border bg-white overflow-hidden shadow-sm">
                  <div className="bg-navy px-5 py-3 flex items-center justify-between gap-3">
                    <h3 className="font-display text-white text-lg font-bold">Volume {volume.volume} · {volume.year}</h3>
                    <p className="font-sans text-sm text-slate-light">{volume.issues.length} Issues · {articleCount} Articles</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-bg-border">
                    {volume.issues.map((issue) => (
                      <button
                        key={issue.id}
                        type="button"
                        onClick={() => setSelectedIssueId(issue.id)}
                        className={`flex items-start gap-3 text-left p-4 transition-colors ${selectedIssueId === issue.id ? 'bg-bg-ice' : 'bg-white hover:bg-bg-cool'}`}
                      >
                        <div className="h-20 w-20 flex-shrink-0 overflow-hidden rounded border border-bg-border">
                          <IssueThumbnail src={issue.coverImage} alt="" volume={issue.raw.volume} issueNumber={issue.issueNumber} />
                        </div>
                        <div className="min-w-0">
                          {/* {selectedIssueId === issue.id && 
                          <span className="inline-block rounded-full bg-navy px-2 py-0.5 text-xs font-sans font-semibold text-white mb-2">Current Issue</span>} */}
                          <p className="font-display text-navy font-bold">Issue {issue.issueNumber}</p>
                          <p className="font-sans text-sm text-slate">{issue.season} {issue.year}</p>
                          <p className="font-sans text-sm text-gray-500 mt-1">{issue.upcoming ? 'Forthcoming' : `${issue.articleCount} articles`}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                </section>
              );
            })}
          </div>

          {selectedIssue && (
            <section className="rounded-lg border border-bg-border bg-white shadow-sm overflow-hidden">
              <div className="bg-bg-ice border-b border-bg-border px-5 py-4 flex items-center justify-between gap-4">
                <h2 className="font-display text-navy text-xl font-bold">{selectedIssue.volume}, Issue {selectedIssue.issueNumber} — Table of Contents</h2>
                <p className="font-sans text-sm text-slate">{selectedIssue.season} {selectedIssue.year}</p>
              </div>
              {selectedIssue.articles.length ? (
                <div className="divide-y divide-bg-border">
                  {selectedIssue.articles.map((article) => (
                    <article key={article.id} className="grid grid-cols-[56px_1fr] sm:grid-cols-[70px_88px_1fr] gap-4 px-5 py-5">
                      <p className="hidden sm:block font-sans text-sm text-slate pt-1">{article.pageRange || '—'}</p>
                      <Link to={`/articles/${article.id}`} className="h-16 w-16 sm:h-[88px] sm:w-[88px] flex-shrink-0 overflow-hidden rounded-lg border border-bg-border">
                        <ArticleThumbnail src={article.imageUrl} alt="" type={article.type} />
                      </Link>
                      <div>
                        <p className="font-sans text-xs uppercase tracking-[0.25em] text-slate font-semibold mb-3">{formatArticleType(article.type)}</p>
                        <Link to={`/articles/${article.id}`} className="font-display text-navy text-lg font-bold hover:text-amber">{article.title}</Link>
                        <p className="font-sans text-sm text-slate mt-1">{article.authors}</p>
                        <div className="flex flex-wrap items-center gap-2 mt-2">
                          <Badge type={article.type} />
                          <Badge openAccess />
                        </div>
                        <div className="flex gap-4 mt-3 font-sans text-sm font-semibold">
                          {article.pdfUrl && <a href={article.pdfUrl} target="_blank" rel="noopener noreferrer" className="text-navy hover:text-amber">PDF</a>}
                          <Link to={`/articles/${article.id}`} className="text-navy hover:text-amber">Abstract</Link>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              ) : (
                <div className="p-10 text-center font-sans text-slate">{selectedIssue.upcoming ? 'This issue is forthcoming.' : 'No articles assigned to this issue yet.'}</div>
              )}
            </section>
          )}
        </main>
      )}
    </>
  );
}

export default Articles;
